import { ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface RichInlineTextProps {
  text: string
  testId?: string
}

const INLINE_ELEMENTS = ['p', 'a', 'strong', 'em', 'del', 'code', 'br']

/** An http(s) link, or null: anything else renders as its text, never as a link. */
export function safeLinkHref(href: string | undefined | null): string | null {
  if (!href) {
    return null
  }
  try {
    const url = new URL(href)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null
  } catch {
    return null
  }
}

function hostLabel(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '')
  } catch {
    return href
  }
}

function textOf(children: ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children)
  }
  if (Array.isArray(children)) {
    return children.map(textOf).join('')
  }
  return ''
}

/**
 * A rich block's text field with its inline markdown rendered: links open in
 * a new tab, bold and italic read as such, and a bare URL shows as its host,
 * so neither raw markdown nor a raw URL ever reaches the reader. Block-level
 * markdown is flattened to its text.
 */
export function RichInlineText(props: RichInlineTextProps) {
  const { text, testId } = props

  return (
    <span data-testid={testId}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        allowedElements={INLINE_ELEMENTS}
        unwrapDisallowed
        components={{
          p: ({ children }) => <>{children}</>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          code: ({ children }) => <code className="rounded bg-muted px-1 text-[0.9em]">{children}</code>,
          a: ({ href, children }) => {
            const safe = safeLinkHref(href)
            if (!safe) {
              return <>{children}</>
            }
            const label = textOf(children)
            const isBareUrl = label === href || label === safe
            return (
              <a
                href={safe}
                target="_blank"
                rel="noopener noreferrer"
                className="text-link underline underline-offset-2 hover:text-link-hover"
                data-testid="thread-rich-inline-link"
              >
                {isBareUrl ? hostLabel(safe) : children}
              </a>
            )
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </span>
  )
}

const MARKDOWN_LINK = /\[[^\]]*\]\((https?:\/\/[^\s)]+)\)/g
const BARE_URL = /(?<![(\w/])https?:\/\/[^\s)<>\]]+/g

/** Every http(s) link in a markdown string, markdown links and bare URLs, in order, deduplicated. */
export function markdownLinkUrls(text: string): Array<string> {
  const found: Array<{ at: number; url: string }> = []
  for (const match of text.matchAll(MARKDOWN_LINK)) {
    found.push({ at: match.index ?? 0, url: match[1] })
  }
  const withoutLinks = text.replace(MARKDOWN_LINK, (whole) => ' '.repeat(whole.length))
  for (const match of withoutLinks.matchAll(BARE_URL)) {
    found.push({ at: match.index ?? 0, url: match[0].replace(/[.,;:!?]+$/, '') })
  }
  found.sort((a, b) => a.at - b.at)
  return Array.from(new Set(found.map((entry) => entry.url)))
}
